// AspectGrid - Visual representation of synastry aspects
import React from 'react';
import { motion } from 'framer-motion';

// Aspect symbol colors based on harmony
const getAspectColor = (harmony) => {
    if (harmony >= 0.9) return 'text-green-400'; // Conjunction (powerful)
    if (harmony >= 0.8) return 'text-emerald-400'; // Trine/Sextile (harmonious)
    if (harmony >= 0.5) return 'text-amber-400'; // Opposition (polarizing)
    return 'text-orange-400'; // Square (challenging)
};

const getAspectBg = (harmony) => {
    if (harmony >= 0.9) return 'bg-green-500/20';
    if (harmony >= 0.8) return 'bg-emerald-500/20';
    if (harmony >= 0.5) return 'bg-amber-500/20';
    return 'bg-orange-500/20';
};

// Area badge colors
const getAreaColor = (area) => {
    const colors = {
        love: 'bg-pink-500/30 text-pink-300',
        passion: 'bg-red-500/30 text-red-300',
        emotional: 'bg-blue-500/30 text-blue-300',
        communication: 'bg-cyan-500/30 text-cyan-300',
        growth: 'bg-green-500/30 text-green-300',
        challenge: 'bg-orange-500/30 text-orange-300',
        other: 'bg-purple-500/30 text-purple-300'
    };
    return colors[area] || colors.other;
};

// Area emoji
const getAreaEmoji = (area) => {
    const emojis = {
        love: '❤️',
        passion: '🔥',
        emotional: '🌙',
        communication: '💬',
        growth: '🌱',
        challenge: '⚡',
        ego: '☀️',
        other: '✨'
    };
    return emojis[area] || emojis.other;
};

export const AspectGrid = ({ aspects = [] }) => {
    if (aspects.length === 0) {
        return (
            <div className="text-center text-purple-400/70 py-8">
                No significant aspects found
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <h3 className="text-sm font-medium text-purple-300/70 text-center">
                Key Aspects
            </h3>

            <div className="grid gap-2">
                {aspects.map((aspect, index) => (
                    <AspectCard key={index} aspect={aspect} index={index} />
                ))}
            </div>
        </div>
    );
};

const AspectCard = ({ aspect, index }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`p-3 rounded-xl border transition-all cursor-pointer ${isExpanded
                    ? 'bg-purple-800/40 border-purple-400/40'
                    : 'bg-purple-900/30 border-purple-500/20 hover:bg-purple-800/30'
                }`}
            onClick={() => setIsExpanded(!isExpanded)}
        >
            {/* Main row */}
            <div className="flex items-center justify-between">
                {/* Planets */}
                <div className="flex items-center gap-2">
                    <span className="text-lg" title={aspect.planetA}>
                        {aspect.symbolA}
                    </span>
                    <span className={`text-lg ${getAspectColor(aspect.harmony)}`}>
                        {aspect.aspectSymbol}
                    </span>
                    <span className="text-lg" title={aspect.planetB}>
                        {aspect.symbolB}
                    </span>
                </div>

                {/* Theme */}
                <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getAreaColor(aspect.area)}`}>
                        {getAreaEmoji(aspect.area)} {aspect.theme}
                    </span>
                    {aspect.isExact && (
                        <span className="text-[10px] text-green-400/70">Exact!</span>
                    )}
                </div>
            </div>

            {/* Expanded details */}
            {isExpanded && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 pt-3 border-t border-purple-500/20 text-sm"
                >
                    <div className="grid grid-cols-2 gap-2 text-purple-300/80">
                        <div>
                            <span className="text-purple-400/60">Person A:</span>{' '}
                            {aspect.planetA} in {aspect.signA}
                        </div>
                        <div>
                            <span className="text-purple-400/60">Person B:</span>{' '}
                            {aspect.planetB} in {aspect.signB}
                        </div>
                    </div>
                    <div className="mt-2 text-purple-300/70 text-xs">
                        <span className={`${getAspectColor(aspect.harmony)}`}>
                            {aspect.aspect}
                        </span>
                        {' '}— {aspect.description}
                        {aspect.orb && (
                            <span className="text-purple-400/50 ml-2">
                                (orb: {aspect.orb.toFixed(1)}°)
                            </span>
                        )}
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default AspectGrid;
